/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState, ReactNode } from 'react';
import type {
  ArtworkImagePayload,
  FrameApplyPayload,
  FramePreloadPayload,
  FramePreloadState,
  SnapshotRequestPayload,
  UnityBridgeEvent,
  UnityInstanceLike,
  ViewerEnvironment,
} from '../types/unity';

type LegacyArtworkPayload = string | ArtworkImagePayload;

const VIEWER_ENVIRONMENTS: ViewerEnvironment[] = [
  'Main Room',
  'Guest Room',
  'Bedroom',
  'Office',
  'Gallery',
];

interface UnityContextType {
  unityInstance: UnityInstanceLike | null;
  setUnityInstance: (instance: UnityInstanceLike | null) => void;
  isUnityReady: boolean;
  isUnityLoading: boolean;
  loadingProgress: number; // 0..1
  lastUnityEvent: UnityBridgeEvent | null;
  lastUnityError: string | null;
  loadedArtwork: UnityBridgeEvent['data'] | null;
  framePreload: FramePreloadState;
  snapshot: UnityBridgeEvent['data'] | null;
  isSnapshotting: boolean;
  snapshotStatus: 'idle' | 'pending' | 'complete' | 'error';
  snapshotUrl: string | null;
  snapshotError: string | null;
  environments: ViewerEnvironment[];
  currentEnvironment: ViewerEnvironment;
  loadPiece: (payload: LegacyArtworkPayload) => void;
  sendArtworkImagePayload: (payload: ArtworkImagePayload) => void;
  nextCamera: () => void;
  applyFrame: (payload: FrameApplyPayload) => void;
  clearFrame: () => void;
  preloadFrames: (addressableKeys: string[] | FramePreloadPayload) => void;
  setEnvironment: (environment: ViewerEnvironment) => void;
  requestSnapshot: (payload?: SnapshotRequestPayload) => boolean;
  resetSnapshot: () => void;
  onUnityEvent: (payload: string | UnityBridgeEvent) => void;
  onUnityReady: (instance: UnityInstanceLike) => void;
  onUnityError: (error: string) => void;
  onUnityProgress: (progress: number) => void;
}

const UnityContext = createContext<UnityContextType | undefined>(undefined);

export const useUnity = () => {
  const context = useContext(UnityContext);
  if (context === undefined) {
    throw new Error('useUnity must be used within a UnityProvider');
  }
  return context;
};

interface UnityProviderProps {
  children: ReactNode;
  pieceId?: string; // Optional initial piece id (not used here)
  viewMode?: '2d' | '3d'; // Accepted but not used here (kept to avoid breaking callers)
  onViewModeChange?: (mode: '2d' | '3d') => void; // Accepted but not used here
}

export const UnityProvider: React.FC<UnityProviderProps> = ({ children }) => {
  const [unityInstance, setUnityInstance] = useState<UnityInstanceLike | null>(null);
  const [isUnityReady, setIsUnityReady] = useState(false);
  const [isUnityLoading, setIsUnityLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [lastUnityEvent, setLastUnityEvent] = useState<UnityBridgeEvent | null>(null);
  const [lastUnityError, setLastUnityError] = useState<string | null>(null);
  const [loadedArtwork, setLoadedArtwork] = useState<UnityBridgeEvent['data'] | null>(null);
  const [snapshot, setSnapshot] = useState<UnityBridgeEvent['data'] | null>(null);
  const [snapshotStatus, setSnapshotStatus] = useState<'idle' | 'pending' | 'complete' | 'error'>('idle');
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const snapshotRequestIdRef = useRef(0);
  const [currentEnvironment, setCurrentEnvironment] = useState<ViewerEnvironment>('Main Room');
  const [framePreload, setFramePreload] = useState<FramePreloadState>({
    status: 'idle',
    percent: 0,
    keyCount: 0,
    keys: [],
  });

  const sendToUnity = useCallback((gameObject: string, methodName: string, parameter = '') => {
    if (unityInstance && isUnityReady) {
      try {
        unityInstance.SendMessage(gameObject, methodName, parameter);
        return true;
      } catch (err) {
        console.warn(`Failed to send ${gameObject}.${methodName} to Unity:`, err);
        return false;
      }
    }

    console.warn(`Unity instance not ready, cannot send ${gameObject}.${methodName}:`, parameter);
    return false;
  }, [isUnityReady, unityInstance]);

  const sendArtworkImagePayload = useCallback((payload: ArtworkImagePayload) => {
    sendToUnity('Artwork', 'OnImageUrl', JSON.stringify(payload));
  }, [sendToUnity]);

  const loadPiece = useCallback((payload: LegacyArtworkPayload) => {
    const normalizedPayload: ArtworkImagePayload = typeof payload === 'string' ? { url: payload } : payload;
    sendArtworkImagePayload(normalizedPayload);
  }, [sendArtworkImagePayload]);

  const nextCamera = useCallback(() => {
    sendToUnity('CameraPositions', 'NextItem');
  }, [sendToUnity]);

  const applyFrame = useCallback((payload: FrameApplyPayload) => {
    sendToUnity('Artwork', 'ApplyFrame', JSON.stringify(payload));
  }, [sendToUnity]);

  const clearFrame = useCallback(() => {
    sendToUnity('Artwork', 'ClearFrame');
  }, [sendToUnity]);

  const preloadFrames = useCallback((addressableKeys: string[] | FramePreloadPayload) => {
    const keys = (Array.isArray(addressableKeys) ? addressableKeys : addressableKeys.keys)
      .map((key) => key.trim())
      .filter(Boolean);
    const uniqueKeys = Array.from(new Set(keys)).sort();

    if (uniqueKeys.length === 0) {
      return;
    }

    setFramePreload({
      status: 'downloading',
      percent: 0,
      keyCount: uniqueKeys.length,
      keys: uniqueKeys,
    });
    sendToUnity('Artwork', 'PreloadFrames', JSON.stringify({ keys: uniqueKeys }));
  }, [sendToUnity]);

  const setEnvironment = useCallback((environment: ViewerEnvironment) => {
    const sent = sendToUnity('SceneObject', 'SetScene', environment);
    if (sent) {
      setCurrentEnvironment(environment);
    }
    return sent;
  }, [sendToUnity]);

  const requestSnapshot = useCallback((payload: SnapshotRequestPayload = {}) => {
    const requestId = snapshotRequestIdRef.current + 1;
    snapshotRequestIdRef.current = requestId;
    setSnapshotStatus('pending');
    setSnapshotError(null);
    setSnapshotUrl(null);
    const sent = sendToUnity('Snapshot', 'Request', JSON.stringify(payload));
    if (!sent) {
      setSnapshotStatus('error');
      setSnapshotError('Unity is not ready for snapshots.');
      return false;
    }

    window.setTimeout(() => {
      if (snapshotRequestIdRef.current !== requestId) {
        return;
      }

      try {
        const canvas = document.getElementById('unity-canvas') as HTMLCanvasElement | null;
        const dataUrl = canvas?.toDataURL('image/png');
        if (!dataUrl) {
          throw new Error('Unity canvas is not available.');
        }
        const event: UnityBridgeEvent = {
          name: 'Snapshot.Ready',
          data: {
            url: dataUrl,
            mimeType: 'image/png',
            includeFrame: payload.includeFrame,
            source: 'web-canvas-fallback',
          },
        };
        snapshotRequestIdRef.current += 1;
        setSnapshot(event.data ?? null);
        setSnapshotStatus('complete');
        setSnapshotUrl(dataUrl);
        setSnapshotError(null);
      } catch (error) {
        if (snapshotRequestIdRef.current !== requestId) {
          return;
        }
        setSnapshotStatus('error');
        setSnapshotError(error instanceof Error ? error.message : 'Snapshot failed.');
      }
    }, 1200);

    return sent;
  }, [sendToUnity]);

  const resetSnapshot = useCallback(() => {
    snapshotRequestIdRef.current += 1;
    setSnapshot(null);
    setSnapshotStatus('idle');
    setSnapshotUrl(null);
    setSnapshotError(null);
  }, []);

  const handleUnityReady = useCallback((instance: UnityInstanceLike) => {
    setUnityInstance(instance);
    setIsUnityReady(true);
    setIsUnityLoading(false);
    setLoadingProgress(1);
    console.log('Unity instance is ready');
  }, []);

  const handleUnityError = useCallback((error: string) => {
    console.error('Unity error:', error);
    setLastUnityError(error);
    setIsUnityReady(false);
    // keep isUnityLoading as-is; errors may occur during/after load
  }, []);

  const handleUnityProgress = useCallback((progress: number) => {
    // Clamp and update progress + loading flag
    const p = Math.max(0, Math.min(1, progress));
    setLoadingProgress(p);
    // Consider "loading" until fully complete and ready callback occurs
    if (p < 1) setIsUnityLoading(true);
  }, []);

  const handleUnityEvent = useCallback((payload: string | UnityBridgeEvent) => {
    let event: UnityBridgeEvent | null = null;

    if (typeof payload === 'string') {
      try {
        event = JSON.parse(payload) as UnityBridgeEvent;
      } catch (error) {
        console.warn('Invalid Unity event JSON:', payload, error);
        return;
      }
    } else {
      event = payload;
    }

    if (!event?.name) {
      console.warn('Unity event missing name:', payload);
      return;
    }

    setLastUnityEvent(event);

    switch (event.name) {
      case 'Runtime.Ready':
        setIsUnityReady(true);
        setIsUnityLoading(false);
        setLoadingProgress(1);
        setLastUnityError(null);
        break;
      case 'Artwork.Loaded':
        setLoadedArtwork(event.data ?? null);
        break;
      case 'FramePreload.Progress':
        setFramePreload({
          status: event.data.status === 'cached' ? 'cached' : 'downloading',
          percent: Math.max(0, Math.min(1, event.data.percent ?? 0)),
          keyCount: event.data.keyCount ?? event.data.keys?.length ?? 0,
          keys: event.data.keys ?? [],
          downloadedBytes: event.data.downloadedBytes,
          totalBytes: event.data.totalBytes,
        });
        break;
      case 'FramePreload.Complete':
        setFramePreload({
          status: event.data.success === false ? 'failed' : event.data.status === 'cached' ? 'cached' : 'complete',
          percent: event.data.success === false ? 0 : 1,
          keyCount: event.data.keyCount ?? event.data.keys?.length ?? 0,
          keys: event.data.keys ?? [],
          totalBytes: event.data.totalBytes,
          error: event.data.error ?? undefined,
        });
        if (event.data.success === false && event.data.error) {
          setLastUnityError(event.data.error);
        }
        break;
      case 'FramePreload.Failed': {
        const error = event.data?.error ?? event.data?.message ?? 'Frame preload failed.';
        setFramePreload({
          status: 'failed',
          percent: 0,
          keyCount: event.data?.keys?.length ?? 0,
          keys: event.data?.keys ?? [],
          error,
        });
        setLastUnityError(error);
        break;
      }
      case 'Error': {
        const error = event.data?.message ?? event.data?.error ?? 'Unity reported an error.';
        setLastUnityError(error);
        if (snapshotStatus === 'pending') {
          setSnapshotStatus('error');
          setSnapshotError(error);
        }
        console.error('Unity event error:', event.data ?? error);
        break;
      }
      case 'Snapshot.Ready':
        snapshotRequestIdRef.current += 1;
        setSnapshot(event.data ?? null);
        setSnapshotStatus('complete');
        setSnapshotUrl(event.data?.url ?? event.data?.path ?? event.data?.tempFilePath ?? event.data?.base64 ?? null);
        setSnapshotError(null);
        break;
      default:
        break;
    }
  }, [snapshotStatus]);

  const value: UnityContextType = useMemo(() => ({
    unityInstance,
    setUnityInstance,
    isUnityReady,
    isUnityLoading,
    loadingProgress,
    lastUnityEvent,
    lastUnityError,
    loadedArtwork,
    framePreload,
    snapshot,
    isSnapshotting: snapshotStatus === 'pending',
    snapshotStatus,
    snapshotUrl,
    snapshotError,
    environments: VIEWER_ENVIRONMENTS,
    currentEnvironment,
    loadPiece,
    sendArtworkImagePayload,
    nextCamera,
    applyFrame,
    clearFrame,
    preloadFrames,
    setEnvironment,
    requestSnapshot,
    resetSnapshot,
    onUnityEvent: handleUnityEvent,
    onUnityReady: handleUnityReady,
    onUnityError: handleUnityError,
    onUnityProgress: handleUnityProgress,
  }), [
    applyFrame,
    clearFrame,
    currentEnvironment,
    framePreload,
    handleUnityError,
    handleUnityEvent,
    handleUnityProgress,
    handleUnityReady,
    isUnityLoading,
    isUnityReady,
    lastUnityError,
    lastUnityEvent,
    loadedArtwork,
    loadingProgress,
    loadPiece,
    nextCamera,
    preloadFrames,
    requestSnapshot,
    resetSnapshot,
    sendArtworkImagePayload,
    setEnvironment,
    snapshot,
    snapshotError,
    snapshotStatus,
    snapshotUrl,
    unityInstance,
  ]);

  return (
    <UnityContext.Provider value={value}>
      {children}
    </UnityContext.Provider>
  );
};
